import { useState } from "react";
import { ChevronDown, Copy, CheckCircle2, AlertTriangle, AlertOctagon, ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_STYLES = {
  critical: {
    badge: "bg-red-500/10 text-red-400 border-red-500/30",
    icon: AlertOctagon,
    iconClass: "text-red-400",
    accent: "border-red-500/30",
    label: "Critical",
  },
  high: {
    badge: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    icon: ShieldAlert,
    iconClass: "text-orange-400",
    accent: "border-orange-500/25",
    label: "High",
  },
  medium: {
    badge: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    icon: AlertTriangle,
    iconClass: "text-amber-400",
    accent: "border-amber-500/20",
    label: "Medium",
  },
  passed: {
    badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    icon: CheckCircle2,
    iconClass: "text-emerald-400",
    accent: "border-emerald-500/20",
    label: "Passed",
  },
};

export const FindingCard = ({ finding }) => {
  const [open, setOpen] = useState(finding.severity !== "passed");
  const [copied, setCopied] = useState(false);
  const style = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.medium;
  const Icon = style.icon;

  const copyFix = async () => {
    try {
      await navigator.clipboard.writeText(finding.fix);
      setCopied(true);
      toast.success("Fix copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <div
      className={`rounded-xl border bg-slate-900/70 ${style.accent} overflow-hidden transition-colors`}
      data-testid={`finding-card-${finding.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-4 p-5 text-left hover:bg-slate-900/90 transition-colors"
        data-testid={`finding-toggle-${finding.id}`}
      >
        <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-950 border border-slate-800`}>
          <Icon className={`h-4.5 w-4.5 ${style.iconClass}`} strokeWidth={2.2} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display font-semibold text-slate-100 text-base sm:text-lg">
              {finding.name}
            </h3>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${style.badge}`}
            >
              {style.label}
            </span>
            <span className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">
              {finding.category}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-300">{finding.verdict}</p>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-slate-800/80 bg-slate-950/40 px-5 py-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1.5">
              What this means
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{finding.explanation}</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1.5">
              Why it matters
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{finding.impact}</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">
                Recommended fix
              </div>
              <button
                type="button"
                onClick={copyFix}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-800 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors"
                data-testid={`copy-fix-button-${finding.id}`}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="font-mono text-xs sm:text-[13px] text-cyan-300 bg-slate-950 border border-slate-800 rounded-lg p-3 whitespace-pre-wrap break-words">
{finding.fix}
            </pre>
          </div>
          {finding.evidence && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
                Evidence
              </div>
              <p className="font-mono text-xs text-slate-400 break-all">{finding.evidence}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FindingCard;
